import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { ADMIN_AUTH_EVENT, ADMIN_LOGOUT_EVENT, SYNC_CLEARED_EVENT } from "@/constants/admin";
import { hasAdminSession, scheduleCloudPut } from "@/lib/cloud-admin-sync";

type AvailabilityMap = Record<string, boolean>;

/** Whole-product key: `slug`. Per-flavor OOS: `slug::Flavor Name`. */
export function flavorAvailabilityKey(slug: string, flavor: string) {
  return `${slug}::${flavor.trim()}`;
}

type AvailabilityCtx = {
  isAvailable: (slug: string) => boolean;
  isFlavorAvailable: (slug: string, flavor: string) => boolean;
  productHasShopStock: (slug: string, flavors?: string[] | null) => boolean;
  overrides: AvailabilityMap;
  setAvailable: (slug: string, available: boolean) => void;
  setFlavorAvailable: (slug: string, flavor: string, available: boolean) => void;
  /** Drop whole-product + per-flavor overrides for a slug (e.g. after permanent product delete). */
  clearAvailabilityForProduct: (slug: string) => void;
  resetAvailability: () => void;
};

export const AVAILABILITY_STORAGE_KEY = "jmanatx_availability";

function load(): AvailabilityMap {
  try {
    const raw = localStorage.getItem(AVAILABILITY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function persist(map: AvailabilityMap) {
  localStorage.setItem(AVAILABILITY_STORAGE_KEY, JSON.stringify(map));
}

const Ctx = createContext<AvailabilityCtx>({
  isAvailable: () => true,
  isFlavorAvailable: () => true,
  productHasShopStock: () => true,
  overrides: {},
  setAvailable: () => {},
  setFlavorAvailable: () => {},
  clearAvailabilityForProduct: () => {},
  resetAvailability: () => {},
});

export function AvailabilityProvider({ children }: { children: ReactNode }) {
  const initialLocal = useRef(load());
  const [overrides, setOverrides] = useState<AvailabilityMap>(() => initialLocal.current);
  const putTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [remoteReady, setRemoteReady] = useState(false);
  const [authTick, setAuthTick] = useState(0);
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;

  useEffect(() => {
    const onAuth = () => setAuthTick((n) => n + 1);
    const onLogout = () => clearTimeout(putTimer.current);
    window.addEventListener(ADMIN_AUTH_EVENT, onAuth);
    window.addEventListener(ADMIN_LOGOUT_EVENT, onLogout);
    return () => {
      window.removeEventListener(ADMIN_AUTH_EVENT, onAuth);
      window.removeEventListener(ADMIN_LOGOUT_EVENT, onLogout);
    };
  }, []);

  useEffect(() => {
    function onCloudSynced() {
      initialLocal.current = { ...overridesRef.current };
    }
    window.addEventListener(SYNC_CLEARED_EVENT, onCloudSynced);
    return () => window.removeEventListener(SYNC_CLEARED_EVENT, onCloudSynced);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/availability", { cache: "no-store" });
        if (!r.ok || cancelled) return;
        const server = (await r.json()) as AvailabilityMap;
        setOverrides((prev) => {
          if (JSON.stringify(prev) === JSON.stringify(initialLocal.current)) return server;
          return prev;
        });
      } catch {
        /* offline or no Functions */
      } finally {
        if (!cancelled) setRemoteReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    persist(overrides);
  }, [overrides]);

  useEffect(() => {
    if (!remoteReady || !hasAdminSession()) return;
    scheduleCloudPut(putTimer, "/api/availability", { overrides });
    return () => clearTimeout(putTimer.current);
  }, [overrides, remoteReady, authTick]);

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === AVAILABILITY_STORAGE_KEY) {
        try {
          setOverrides(e.newValue ? JSON.parse(e.newValue) : {});
        } catch {
          /* ignore corrupt data */
        }
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setAvailable = useCallback((slug: string, available: boolean) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (available) {
        delete next[slug];
      } else {
        next[slug] = false;
      }
      return next;
    });
  }, []);

  const clearAvailabilityForProduct = useCallback((slug: string) => {
    setOverrides((prev) => {
      const next = { ...prev };
      const prefix = `${slug}::`;
      for (const k of Object.keys(next)) {
        if (k === slug || k.startsWith(prefix)) delete next[k];
      }
      return next;
    });
  }, []);

  const setFlavorAvailable = useCallback((slug: string, flavor: string, available: boolean) => {
    const trimmed = flavor.trim();
    if (!trimmed) return;
    const k = flavorAvailabilityKey(slug, trimmed);
    setOverrides((prev) => {
      const next = { ...prev };
      if (available) delete next[k];
      else next[k] = false;
      return next;
    });
  }, []);

  const isAvailable = useCallback(
    (slug: string): boolean => overrides[slug] !== false,
    [overrides],
  );

  const isFlavorAvailable = useCallback(
    (slug: string, flavor: string): boolean => {
      if (overrides[slug] === false) return false;
      const t = flavor.trim();
      if (!t) return true;
      return overrides[flavorAvailabilityKey(slug, t)] !== false;
    },
    [overrides],
  );

  const productHasShopStock = useCallback(
    (slug: string, flavors?: string[] | null): boolean => {
      if (overrides[slug] === false) return false;
      if (!flavors?.length) return true;
      return flavors.some((fl) => {
        const t = fl.trim();
        if (!t) return true;
        return overrides[flavorAvailabilityKey(slug, t)] !== false;
      });
    },
    [overrides],
  );

  const resetAvailability = useCallback(() => {
    setOverrides({});
  }, []);

  return (
    <Ctx.Provider
      value={{
        isAvailable,
        isFlavorAvailable,
        productHasShopStock,
        overrides,
        setAvailable,
        setFlavorAvailable,
        clearAvailabilityForProduct,
        resetAvailability,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAvailability() {
  return useContext(Ctx);
}
